import { LinearProgress, Popover } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { ReactNode } from 'react';
import { PopupButton, Table, TableContainer } from './styles';
import PageLoader from './PageLoader';
import React from 'react';
import { NoDataMessage } from 'components/NoDataMessage/styles';

export type ColumnProps = {
  name: string | ReactNode;
  minWidth?: string;
  align?: "center" | "left" | "right";
  sortValues?: boolean;
};

type TableProps = {
  loading: boolean;
  columns: ColumnProps[];
  children: ReactNode;
  actions?: {
    handleSortClick: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, column: string) => void;
    handleSort: (order: string) => void;
    popupAnchor: any;
    setPopupAnchor: React.Dispatch<React.SetStateAction<any>>;
  };
  isPopup?: boolean;
  hasBorderRadius?: boolean;
  noHeader?: boolean;
  contentHeight?: string;
  loadingType?: 'full' | 'inline';
};

export function DataTable({
  loading,
  columns,
  children,
  actions,
  hasBorderRadius = true,
  noHeader,
  isPopup = false,
  contentHeight = '50vh',
  loadingType = 'full'
}: TableProps) {

  const handleClosePopup = () => {
    actions?.setPopupAnchor(null);
  };

  const hasData = React.Children.count(children) > 0;
  const useInlineLoading = loadingType === 'inline';
  const shouldShowTable = useInlineLoading || (!loading && hasData) || isPopup;
  const shouldShowNoData = !loading && !hasData && !useInlineLoading;

  return (
    <>
      <TableContainer>
        {loading && !isPopup && !useInlineLoading && (
          <PageLoader height={contentHeight} />
        )}

        {shouldShowNoData && (
          <NoDataMessage isPopup={isPopup} ht={contentHeight}>
            <h3>Não há dados para exibir :(</h3>
            <p>Verifique os filtros aplicados e tente novamente.</p>
          </NoDataMessage>
        )}

        {shouldShowTable && (
          <Table style={{ position: 'relative' }} hasBorderRadius={hasBorderRadius} noHeader={noHeader} actions={!!actions}>
            <thead>
              <tr className='headerTable'>
                {columns.map((column, index) => (
                  <th key={index} className={`col`} align={column.align} style={{ minWidth: column.minWidth || '150px', flex: 1 }}>
                    <div className='columnAttributes'>
                      {column.name}
                      {actions && column.sortValues && (
                        <ArrowDownward
                          className={`arrowIcon`}
                          onClick={(e) => actions.handleSortClick(e, String(column.name))}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {useInlineLoading && loading && (
              <LinearProgress 
                color="success" 
                style={{ 
                  position: 'absolute', 
                  width: '100%',
                  top: '48px',
                  zIndex: 1
                }} 
              />
            )}
            <tbody className='bodyTable' style={{ opacity: useInlineLoading && loading && hasData ? 0.5 : 1, transition: 'opacity 0.2s ease-in-out' }}>
            {loading && !hasData && useInlineLoading ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '180px', color: '#666' }}>
                    <h3>Carregando...</h3>
                  </td>
              </tr>


              ) : !hasData && !loading && useInlineLoading ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', color: '#666' }}>
                    <NoDataMessage isPopup={isPopup} ht={contentHeight}>
                      <h3>Não há dados para exibir :(</h3>
                      <p>Verifique os filtros aplicados e tente novamente.</p>
                    </NoDataMessage>
                  </td>
                </tr>
              ) : (
                children
              )}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <Popover
        open={Boolean(actions?.popupAnchor)}
        anchorEl={actions?.popupAnchor}
        onClose={handleClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopupButton onClick={() => actions?.handleSort('asc')}>
          <ArrowUpward className='arrowIcon' />
          Ordem crescente
        </PopupButton>
        <PopupButton onClick={() => actions?.handleSort('desc')}>
          <ArrowDownward className='arrowIcon' />
          Ordem decrescente
        </PopupButton>
      </Popover>
    </>
  );
}
